import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isUndefined } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { settingsStoreToken } from '../../../cross-team-shared/state/setting-state';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { MainCameraService } from '../../render/cameras/main-camera.service';
import { RenderTriggerService } from '../../render/render-trigger.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import {
  backViewRegionName,
  mainViewRegionName,
  sideViewRegionName,
  topViewRegionName,
} from '../../state/view-region-state';
import {
  editPanelToolbarHeight,
  rescaleAllToggleHeight,
} from '../view-regions.service';
import { MainPointCloudRendererCursorService } from './main-point-cloud-renderer-cursor.service';

@Component({
  selector: 'h3-main-point-cloud-renderer',
  templateUrl: './main-point-cloud-renderer.component.html',
  styleUrls: ['./main-point-cloud-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RenderTriggerService],
  standalone: false,
})
export class MainPointCloudRendererComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  /**
   * It is unusual to have inputs other than connectors across team
   * boundaries. Normally component size is set directly by the core team, e.g.
   * via css. This is a convenient alternative, so that we get these values into
   * our JS.
   */
  @Input({ required: true }) size!: Point2D;
  @Input() isThumbnail = false;
  @Input({ required: true }) withSideViews!: boolean;
  @Input({ required: true }) isSingleFrameTask!: boolean;

  @ViewChild('mainView', { read: ElementRef })
  mainViewRef!: ElementRef<HTMLElement>;

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly mainPointCloudCssPropertyService = inject(
    MainPointCloudRendererCursorService,
  );
  private readonly renderTriggerService = inject(RenderTriggerService);
  private readonly shellConnector = inject(shellConnectorToken);
  private readonly settingsStore = inject(settingsStoreToken);
  private readonly store = inject(PointCloudContentStore);
  private readonly mainCameraService = inject(MainCameraService);

  protected isNewEditPanel =
    this.shellConnector.hasFeatureFlag('new-edit-panel');
  protected toolbarHeight = this.isNewEditPanel
    ? editPanelToolbarHeight
    : rescaleAllToggleHeight;

  protected sideViewsHidden$!: Observable<boolean>;

  protected size$!: Observable<Point2D>;

  protected topViewHiddenInSplitView$ = this.settingsStore(
    'topViewHiddenInSplitView',
  ).$;
  protected topViewHidden$!: Observable<boolean>;

  protected hideRescaleAllToggle$!: Observable<boolean>;
  protected hasBottomEditPanels$ = this.store('editPanel')('location').$.pipe(
    map((location) => location === 'bottom'),
  );

  protected isEditPanelMinimized$ = combineLatest([
    this.hasBottomEditPanels$,
    this.store('editPanel')('isExpanded').$,
  ]).pipe(
    map(
      ([hasBottomEditPanels, isEditPanelExpanded]) =>
        hasBottomEditPanels && !isEditPanelExpanded,
    ),
  );

  protected viewRegionNames$!: Observable<string[]>;

  private outputName!: string;

  ngOnInit() {
    this.outputName = this.store.state().output.name;
    this.size$ = this.getInput$('size').pipe(filter(isDefined));

    this.sideViewsHidden$ = this.areSideViewsHidden$();

    this.hideRescaleAllToggle$ = combineLatest([
      this.sideViewsHidden$,
      this.store('readOnly').$,
      this.hasBottomEditPanels$,
    ]).pipe(
      map(
        ([areSideViewsHidden, readOnly, hasBottomEditPanels]) =>
          this.isNewEditPanel ||
          areSideViewsHidden ||
          readOnly ||
          hasBottomEditPanels,
      ),
    );

    this.viewRegionNames$ = this.getViewRegionNames$();
    this.topViewHidden$ = this.isTopViewHidden$();
    this.mainPointCloudCssPropertyService.manageCursor(this);

    this.subscribeTo(this.getInput$('withSideViews'), (withSideViews) => {
      this.store('viewRegions')('hasEditPanelsOpen').set(withSideViews);
    });

    this.subscribeTo(this.getInput$('isThumbnail'), (isThumbnail) => {
      this.store('viewRegions')('isMainViewInThumbnailMode').set(isThumbnail);
    });
  }

  ngAfterViewInit() {
    this.mainCameraService.install(this.mainViewRef.nativeElement);
    this.renderTriggerService.addTrigger(this.store('camera').$);
    this.renderTriggerService.addTrigger(
      this.store('splitViewOutputName').$.pipe(
        filter(isDefined),
        switchMap((outputName) =>
          combineLatest([
            this.store('viewBoxes')(outputName).$,
            this.store('projectionImageSize')(outputName).$,
          ]),
        ),
      ),
    );
    this.renderTriggerService.addTrigger(
      this.store('regionOfInterestSettings').$,
    );
    this.renderTriggerService.addTrigger(
      this.store('viewBoxHighlightEnabled').$,
    );
    this.renderTriggerService.addTrigger(this.store('editPanel').$);

    this.subscribeToSize();
  }

  private subscribeToSize() {
    this.subscribeTo(this.size$, (size) => {
      this.elementRef.nativeElement.style.width = `${size[0]}px`;
      this.elementRef.nativeElement.style.height = `${size[1]}px`;
      this.store('render')('containerSize')(this.outputName).set(size);
    });
  }

  private isTopViewHidden$() {
    return combineLatest([
      this.settingsStore('topViewHiddenInSplitView').$,
      this.hasBottomEditPanels$,
      this.sideViewsHidden$,
    ]).pipe(
      map(
        ([topViewHiddenInSplitView, hasBottomEditPanels, areSideViewsHidden]) =>
          (hasBottomEditPanels && topViewHiddenInSplitView) ||
          areSideViewsHidden,
      ),
    );
  }

  private areSideViewsHidden$() {
    return combineLatest([
      // be aware that observable from inputs always starts with null
      this.getInput$('isThumbnail'),
      this.getInput$('withSideViews'),
      this.store.focusedShape$,
    ]).pipe(
      map(
        ([isThumbnail, withSideViews, focusedShape]) =>
          isUndefined(focusedShape) || isThumbnail || !withSideViews,
      ),
      distinctUntilChanged(),
    );
  }

  // eslint-disable-next-line max-lines-per-function -- needs to contemplate a bunch of stuff
  private getViewRegionNames$() {
    return combineLatest([
      this.topViewHiddenInSplitView$,
      this.sideViewsHidden$,
      this.hasBottomEditPanels$,
      this.isEditPanelMinimized$,
    ]).pipe(
      map(
        ([
          topViewHiddenInSplitView,
          sideViewsHidden,
          hasBottomEditPanels,
          isEditPanelMinimized,
        ]) => {
          if (hasBottomEditPanels) {
            return [
              mainViewRegionName,
              ...this.getViewRegionNamesForBottomEditPanels(
                sideViewsHidden,
                topViewHiddenInSplitView,
                isEditPanelMinimized,
              ),
            ];
          } else {
            return [
              mainViewRegionName,
              ...this.getViewRegionNamesForSideEditPanels(
                sideViewsHidden,
                isEditPanelMinimized,
              ),
            ];
          }
        },
      ),
    );
  }

  private getViewRegionNamesForBottomEditPanels(
    sideViewsHidden: boolean,
    topViewHiddenInSplitView: boolean,
    isEditPanelMinimized: boolean,
  ) {
    if (sideViewsHidden) {
      return [];
    }

    const regions = [];

    if (!topViewHiddenInSplitView) {
      regions.push(topViewRegionName);
    }

    if (!isEditPanelMinimized) {
      regions.push(backViewRegionName, sideViewRegionName);
    }

    return regions;
  }

  private getViewRegionNamesForSideEditPanels(
    sideViewsHidden: boolean,
    isEditPanelMinimized: boolean,
  ) {
    if (!sideViewsHidden && !isEditPanelMinimized) {
      return [topViewRegionName, backViewRegionName, sideViewRegionName];
    }

    return [];
  }
}
